/**
 * Lazy-loaded page components.
 * 
 * Centralizes all React.lazy() imports for route components.
 * This enables better code splitting and reduces App.tsx size.
 */

import { lazy } from 'react';

// Dashboard & Home
export const DashboardPage = lazy(() => import('../pages/Home/index'));

// Tools
export const ToolsLayout = lazy(() => import('../pages/Tools/Layout'));
export const ApiTester = lazy(() => import('../pages/Tools/ApiTester'));
export const UsageAllocator = lazy(() => import('../pages/Tools/UsageAllocator'));
export const WireSpecsTool = lazy(() => import('../pages/Tools/WireSpecsTool'));
export const WireUtilityTool = lazy(() => import('../pages/Tools/WireUtilityTool'));
export const ConduitFillTool = lazy(() => import('../pages/Tools/ConduitFillTool'));

// Dev & Admin
export const DevPage = lazy(() => import('../pages/Dev'));
export const UsersPage = lazy(() => import('../pages/Users'));
export const PlaygroundPage = lazy(() => import('../pages/Playground'));

// Procurement
export const ProcurementPage = lazy(() => import('../pages/Procurement'));

// Estimates
export const EstimatesPage = lazy(() => import('../pages/Estimates'));
export const EstimateDetailRoute = lazy(() => import('../pages/Estimates/EstimateDetailRoute'));
export const EstimatesHomeRoute = lazy(() => import('../pages/Estimates/EstimatesHomeRoute'));

// Decision Center
export const DecisionCenterPage = lazy(() => import('../pages/DecisionCenter'));

// Files & Storage
export const FilesPage = lazy(() => import('../pages/Files'));

// QuickBooks
export const QuickBooksPage = lazy(() => import('../pages/QuickBooks'));

// Payroll (Gusto)
export const PayrollPage = lazy(() => import('../pages/Payroll'));

// Activities & Feed
export const ActivitiesPage = lazy(() => import('../pages/Activities/ActivityIndexPage'));
export const FeedPage = lazy(() => import('../pages/Feed'));
export const ReportsPage = lazy(() => import('../pages/Reports'));
export const ReportDetailPage = lazy(() => import('../pages/Reports/ReportDetailPage'));

// Resources & Guides
export const ResourcesPage = lazy(() => import('../pages/Resources'));
export const SOPsPage = lazy(() => import('../pages/SOPs'));
export const SOPDetailPage = lazy(() => import('../pages/SOPs/Detail'));
export const GuidesPage = lazy(() => import('../pages/Guides'));

// Integrations
export const EnphasePage = lazy(() => import('../pages/Enphase'));
export const GoogleAdsAnalyticsPage = lazy(() => import('../pages/GoogleAdsAnalytics'));

// Deals
export const DealsPage = lazy(() => import('../pages/Deals'));
export const DealDetailPage = lazy(() => import('../pages/Deals/DealDetailPage'));
export const WebProposalPreviewPage = lazy(() => import('../pages/Deals/WebProposalPreview/WebProposalPreviewPage'));

// Projects
export const ProjectsPage = lazy(() => import('../pages/Projects'));
export const ProjectCreatePage = lazy(() => import('../pages/Projects/ProjectCreatePage'));
export const ProjectDetailPage = lazy(() => import('../pages/Projects/ProjectDetailPage'));
export const HecoPage = lazy(() => import('../pages/Projects/HecoPage'));
export const DesignsPage = lazy(() => import('../pages/Projects/DesignsPage'));
export const PermitsPage = lazy(() => import('../pages/Projects/PermitsPage'));

// Service & Maintenance
export const ServiceMaintenancePage = lazy(() => import('../pages/ServiceMaintenance'));
export const ServiceOrderDetailPage = lazy(() => import('../pages/ServiceMaintenance/ServiceOrderDetailPage'));

// Contacts
export const ContactsPage = lazy(() => import('../pages/Contacts'));
export const ContactDetailPage = lazy(() => import('../pages/Contacts/ContactDetailPage'));

// Tasks
export const TasksPage = lazy(() => import('../pages/Tasks'));
export const TaskDetailPage = lazy(() => import('../pages/Tasks/TaskDetailPage'));

// Calendar
export const CalendarPage = lazy(() => import('../pages/Calendar'));
export const CalendarEventDetailPage = lazy(() => import('../pages/Calendar/CalendarEventDetailPage'));

// Employees & Crews
export const EmployeesPage = lazy(() => import('../pages/Employees'));
export const EmployeeDetailPage = lazy(() => import('../pages/Employees/EmployeeDetailPage'));
export const CrewsPage = lazy(() => import('../pages/Crews'));
export const CrewDetailPage = lazy(() => import('../pages/Crews/CrewDetailPage'));

// Planning
export const PlanningPage = lazy(() => import('../pages/Planning'));
export const PlanningDetailPage = lazy(() => import('../pages/Planning/PlanningDetailPage'));
export const LoadCalcsPage = lazy(() => import('../pages/LoadCalcs'));
export const LoadCalcDetailPage = lazy(() => import('../pages/LoadCalcs/LoadCalcDetailPage'));
export const NemAmendmentsPage = lazy(() => import('../pages/NemAmendments'));
export const NemAmendmentDetailPage = lazy(() => import('../pages/NemAmendments/NemAmendmentDetailPage'));

// Messages
export const MessagesPage = lazy(() => import('../pages/Messages'));
export const NotificationsPage = lazy(() => import('../pages/Notifications'));

// Legal & Info
export const TermsPage = lazy(() => import('../pages/Legal/Terms'));
export const PrivacyPage = lazy(() => import('../pages/Legal/Privacy'));
export const AboutPage = lazy(() => import('../pages/About'));
export const ApplicationHistoryPage = lazy(() => import('../pages/ApplicationHistory'));

// CAD
export const LightweightCadPage = lazy(() => import('../pages/Cad'));

// Tradeshows
export const TradeshowsPage = lazy(() => import('../pages/Tradeshows'));
export const TradeshowDetailPage = lazy(() => import('../pages/Tradeshows/TradeshowDetailPage'));

// PDF Test
export const PdfTestPage = lazy(() => import('../pages/PdfTest'));

// Auth
export const LoginPage = lazy(() => import('../pages/Auth/LoginPage'));

export const SolarDesignPage = lazy(() => import('../pages/SolarDesign'));
